import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login } from '@/api/login.ts';
import type { IOrganizationType } from '@/types/user.ts';

type QRLoginOptions = {
	id: string;
	goto: string;
	width?: string;
	height?: string;
	style?: string;
};

type QRLoginFunction = (options: QRLoginOptions) => void;

declare global {
	interface Window {
		QRLogin?: QRLoginFunction;
	}
}

// 飞书 SDK URL
const SDK_URL =
	'https://sf3-cn.feishucdn.com/obj/feishu-static/lark/passport/qrcode/LarkSSOSDKWebQRCode-1.0.3.js';
const REDIRECT_URI = `${location.origin}/login`;

// 异步加载飞书 SDK
const asyncImportSdk = () => {
	return new Promise((resolve, reject) => {
		if (window.QRLogin) {
			resolve(true);
			return;
		}
		const script = document.createElement('script');
		script.async = true;
		script.src = SDK_URL;
		script.onload = () => resolve(true);
		script.onerror = () => reject(false);
		document.head.appendChild(script);
	});
};

/**
 * 飞书登录 Hook
 * 封装飞书登录的所有逻辑，包括二维码创建、组织切换、登录回调等
 */
export const useFeishuLogin = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const code = searchParams.get('code') || '';
	const qrRef = useRef<HTMLDivElement>(null);
	const [loading, setLoading] = useState(false);

	const organizeType = [
		{ label: '雪山之巅', value: 'SnowMountain', id: 'cli_a208f45746f8100b' },
		{ label: '共比邻', value: 'BeLink', id: 'cli_a55142b5a63c500d' },
	];

	const [selectedOrg, setSelectedOrg] = useState(organizeType[0]);

	// 生成飞书授权 URL
	const authUrl = useMemo(() => {
		return `https://passport.feishu.cn/suite/passport/oauth/authorize?client_id=${selectedOrg.id}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&response_type=code`;
	}, [selectedOrg.id]);

	// 一键登录
	const handleLogin = useCallback(() => {
		window.location.href = authUrl;
	}, [authUrl]);

	// 创建二维码
	const createQrCode = useCallback(async () => {
		if (code || !qrRef.current) return;
		// 清空容器
		qrRef.current.innerHTML = '';

		window.QRLogin?.({
			id: 'feishu-qr',
			goto: authUrl,
			width: '260',
			height: '260',
			style: 'width:260px;height:260px;border:none;',
		});
	}, [authUrl, code]);

	// 处理组织切换
	const handleOrgChange = (item: (typeof organizeType)[0]) => {
		setSelectedOrg(item);
		createQrCode();
	};

	// 处理飞书登录回调
	const handleFeishuCallback = useCallback(async () => {
		try {
			setLoading(true);
			const token = localStorage.getItem('token');
			if (!code || token) return;

			// 先清理 URL 中的授权参数，避免后续重复触发
			window.history.replaceState(null, '', REDIRECT_URI);

			const res = await login({
				code,
				redirectUrl: REDIRECT_URI,
				organizationType: selectedOrg.value as IOrganizationType,
			});
			message.success('登录成功');
			localStorage.setItem('token', res.data.token);
			navigate('/');
		} catch (err) {
			console.error('飞书登录失败:', err);
			localStorage.removeItem('token');
			navigate('/login');
		} finally {
			setLoading(false);
		}
	}, [navigate, selectedOrg.value, code]);

	// 异步加载飞书 SDK, 加载成功后创建二维码
	useEffect(() => {
		const token = localStorage.getItem('token');
		if (token) {
			navigate('/');
		} else {
			asyncImportSdk().then(createQrCode);
		}
	}, [createQrCode, navigate]);

	// 处理飞书回调
	useEffect(() => {
		handleFeishuCallback();
	}, [handleFeishuCallback]);

	return {
		qrRef,
		loading,
		selectedOrg,
		organizeType,
		handleOrgChange,
		handleLogin,
	};
};
